// IABF 2027 public registration config.
// Keep amounts and room labels product-owned — UI and Cloud Functions should import these.

import { IABF_REGISTRATION_YEAR } from './iabf-event'

export const REGISTRATION_YEAR = IABF_REGISTRATION_YEAR

/**
 * Early registration for returners + canceled dancers with a held deposit.
 * 11:30am US Eastern (EDT, UTC-4) on August 23, 2026.
 */
export const EARLY_ACCESS_OPENS_AT_ISO = '2026-08-23T11:30:00-04:00'

/** Short dancer-facing label (no year — same festival cycle). */
export const EARLY_ACCESS_OPENS_LABEL = 'August 23 at 11:30am ET'

export function earlyAccessOpensAtDate () {
  return new Date(EARLY_ACCESS_OPENS_AT_ISO)
}

/** @param {Date} [now] */
export function isEarlyAccessTimeReached (now = new Date()) {
  const opens = earlyAccessOpensAtDate()
  if (Number.isNaN(opens.getTime())) {
    return false
  }
  return now.getTime() >= opens.getTime()
}

/** Non-refundable deposit (USD cents). */
export const DEPOSIT_CENTS = 45000

/**
 * Package totals including deposit (USD cents).
 * Dorm $3,650 · Cottage $4,100 · Festival Access (roommate / offsite) $2,200.
 * Same $450 deposit; remaining is what January installments cover.
 */
export const RETREAT_TOTAL_CENTS = 365000
export const COTTAGE_TOTAL_CENTS = 410000
export const ACCESS_TOTAL_CENTS = 220000

/** Access-pass total — prefer festivalTotalCentsForRoom() for new work. */
export const FESTIVAL_TOTAL_CENTS = ACCESS_TOTAL_CENTS

export const REMAINING_CENTS = FESTIVAL_TOTAL_CENTS - DEPOSIT_CENTS

const DORM_ROOMS = ['Standard Dorm', 'ADA Dorm']

/** @param {string} room */
export function festivalTotalCentsForRoom (room) {
  if (room === 'Cottage') {
    return COTTAGE_TOTAL_CENTS
  }
  if (DORM_ROOMS.indexOf(room) !== -1) {
    return RETREAT_TOTAL_CENTS
  }
  return ACCESS_TOTAL_CENTS
}

/** @param {string} room */
export function remainingCentsForRoom (room) {
  return festivalTotalCentsForRoom(room) - DEPOSIT_CENTS
}

export const TERMS_VERSION = 'iabf-2027-v2'

export const CANCEL_POLICY = {
  depositUsd: DEPOSIT_CENTS / 100,
  cancelByLabel: 'April 15, 2027',
  cancelByTimeLabel: 'April 14 at 11:59pm Eastern',
  depositCreditNextYear: 2028
}

/**
 * When dietary self-entry closes for caterer counts (local date start of day).
 * null = open (no close yet). Year-specific — do not reuse a prior festival's date.
 * 2026 closed 2026-06-01; 2027 reopens until a 2027 date is set.
 */
export const DIETARY_ENTRY_DEADLINE = null // e.g. '2027-06-01' when brochure is final

/**
 * Showcase piece registration close (YYYY-MM-DD local). null = open.
 * 2026 closed 2026-06-01. Set a 2027 date when the brochure is final.
 */
export const SHOWCASE_REGISTRATION_DEADLINE = null // e.g. '2027-06-01'

/**
 * Competition entry close (YYYY-MM-DD local). null = open for current year.
 * 2026 closed 2026-06-01. Set a 2027 date when the brochure is final.
 */
export const COMPETITION_REGISTRATION_DEADLINE = null // e.g. '2027-06-01'

/**
 * Last day dancers can self-serve change which piece competes (and re-do survey).
 * null = open until final divisions are set. After this, email hello@iabf.dance.
 * 2026 used competition registration close; set a 2027 date when divisions lock.
 */
export const COMPETITION_PIECE_CHANGE_DEADLINE = null // e.g. '2027-06-15'

/**
 * Guest meal / banquet registration close. null = open for current year.
 * 2026 closed 2026-06-01.
 */
export const GUEST_REGISTRATION_DEADLINE = null // e.g. '2027-06-01'

/**
 * Airport carpool dancer signup close. null = open for current year.
 * 2026 was closed (hard-flag) before festival; 2027 reopens until set.
 */
export const CARPOOL_REGISTRATION_DEADLINE = null // e.g. '2027-06-15'

/**
 * Last day dancers can self-serve change/cancel carpool without emailing.
 * 2026: July 1 (charge date). null for current year = self-serve while signup is open.
 */
export const CARPOOL_CHANGE_DEADLINE = null // e.g. '2027-06-15'

/**
 * Live overrides from Firestore (iabf-config-{year}/deadlines), applied at runtime
 * so admin can set dates without a redeploy. Keys match iabf-deadlines catalog.
 */
let deadlineOverrides = {}

/** @param {Record<string, string|null|undefined>} overrides */
export function setRegistrationDeadlineOverrides (overrides) {
  deadlineOverrides = overrides && typeof overrides === 'object' ? { ...overrides } : {}
}

export function getRegistrationDeadlineOverrides () {
  return { ...deadlineOverrides }
}

function effectiveIso (key, codeDefault) {
  if (Object.prototype.hasOwnProperty.call(deadlineOverrides, key)) {
    const v = deadlineOverrides[key]
    if (v === '' || v === undefined || v === null) {
      return null
    }
    return String(v).slice(0, 10)
  }
  return codeDefault
}

/** Shared year-window helper: closed after deadline, with 2026 legacy lock. */
function isYearWindowClosed (festivalYear, deadlineIso, legacy2026Iso) {
  const y = Number(festivalYear) || REGISTRATION_YEAR
  if (y === 2026) {
    return new Date() >= new Date(legacy2026Iso + 'T00:00:00')
  }
  if (y === REGISTRATION_YEAR || y > 2026) {
    if (!deadlineIso) {
      return false
    }
    return new Date() >= new Date(deadlineIso + 'T00:00:00')
  }
  // Older archive years: treat as closed
  if (y < 2026) {
    return true
  }
  return false
}

/** Whether dietary forms are closed for the given festival year. */
export function isDietaryEntryClosed (festivalYear = REGISTRATION_YEAR) {
  return isYearWindowClosed(
    festivalYear,
    effectiveIso('dietaryEntry', DIETARY_ENTRY_DEADLINE),
    '2026-06-01'
  )
}

/** Whether dancers can still register new showcase pieces. */
export function isShowcaseRegistrationClosed (festivalYear = REGISTRATION_YEAR) {
  return isYearWindowClosed(
    festivalYear,
    effectiveIso('showcaseRegistration', SHOWCASE_REGISTRATION_DEADLINE),
    '2026-06-01'
  )
}

/** Whether competition registration is closed for the given festival year. */
export function isCompetitionRegistrationClosed (festivalYear = REGISTRATION_YEAR) {
  return isYearWindowClosed(
    festivalYear,
    effectiveIso('competitionRegistration', COMPETITION_REGISTRATION_DEADLINE),
    '2026-06-01'
  )
}

/**
 * Whether dancers can still change their competition piece (and re-do survey).
 * After the deadline, they email the IABF team.
 */
export function isCompetitionPieceChangeClosed (festivalYear = REGISTRATION_YEAR) {
  return isYearWindowClosed(
    festivalYear,
    effectiveIso('competitionPieceChange', COMPETITION_PIECE_CHANGE_DEADLINE),
    '2026-06-01'
  )
}

/** Whether guest meal/banquet registration is closed. */
export function isGuestRegistrationClosed (festivalYear = REGISTRATION_YEAR) {
  return isYearWindowClosed(
    festivalYear,
    effectiveIso('guestRegistration', GUEST_REGISTRATION_DEADLINE),
    '2026-06-01'
  )
}

/** Whether airport carpool signup is closed for dancers. */
export function isCarpoolRegistrationClosed (festivalYear = REGISTRATION_YEAR) {
  // 2026: closed for the season (legacy hard close before festival)
  const y = Number(festivalYear) || REGISTRATION_YEAR
  if (y === 2026) {
    return true
  }
  return isYearWindowClosed(
    festivalYear,
    effectiveIso('carpoolRegistration', CARPOOL_REGISTRATION_DEADLINE),
    '2026-06-01'
  )
}

/**
 * Whether dancers can still change/cancel carpool themselves.
 * After this, they email hello@iabf.dance.
 */
export function isCarpoolSelfServiceOpen (festivalYear = REGISTRATION_YEAR) {
  if (isCarpoolRegistrationClosed(festivalYear)) {
    return false
  }
  const y = Number(festivalYear) || REGISTRATION_YEAR
  if (y === 2026) {
    return new Date() < new Date('2026-07-01T00:00:00')
  }
  const changeDeadline = effectiveIso('carpoolChange', CARPOOL_CHANGE_DEADLINE)
  if (!changeDeadline) {
    return true
  }
  return new Date() < new Date(changeDeadline + 'T00:00:00')
}

/** Public policies page (cancellation, etc.) — single source of truth for dancers. */
export const IABF_POLICIES_URL = 'https://www.internationaladultballetfestival.com/policies'

export const ROOM_OPTIONS = [
  {
    value: 'Standard Dorm',
    title: 'Standard Dorm',
    sub: 'Your own room · double bed · private bathroom',
    icon: '🛏'
  },
  {
    value: 'ADA Dorm',
    title: 'Larger Dorm',
    sub: 'Your own room · double bed and single pull-out bed · private bathroom',
    icon: '🛏'
  },
  {
    value: 'Cottage',
    title: 'Cottage',
    sub: 'Your own cottage · double bed + pull-out double · private bathroom',
    icon: '🏡'
  },
  {
    value: 'Staying with another dancer',
    title: 'Stay onsite with a friend',
    sub: 'Share their onsite room',
    icon: '👥',
    requiresRoommateName: true
  },
  {
    value: 'Staying offsite',
    title: 'I’ll stay offsite nearby',
    sub: 'Book your own place · free 2× daily shuttle from SpringHill Suites, New Smyrna',
    icon: '🏨'
  }
]

/** Onsite rooms that consume a physical room inventory slot. */
export const ONSITE_ROOM_LIMIT_KEYS = ['Standard Dorm', 'ADA Dorm', 'Cottage']

/** All public registration ticket types. */
export const ROOM_CAPACITY_KEYS = ROOM_OPTIONS.map(o => o.value)

/** Dancer-facing stay name. */
export function lodgingChoiceTitle (room) {
  const opt = ROOM_OPTIONS.find(o => o.value === room)
  return opt ? opt.title : String(room || '')
}

/** Admin capacity / set-aside labels. */
export const ONSITE_ROOM_ADMIN_OPTIONS = ONSITE_ROOM_LIMIT_KEYS.map(value => {
  const opt = ROOM_OPTIONS.find(o => o.value === value)
  return { value, title: opt ? opt.title : value }
})

/**
 * Default caps for physical onsite rooms only.
 * null = unlimited until admin sets a limit on Deadlines & access.
 * Offsite / roommate do not use these — they only count toward total tickets.
 * ADA Dorm is its own inventory (not folded into Standard Dorm).
 */
export const DEFAULT_ROOM_LIMITS = {
  'Standard Dorm': null,
  'ADA Dorm': null,
  Cottage: null
}

/**
 * Overall festival ticket cap (every registration counts: onsite, roommate, offsite).
 * null = unlimited until set in admin.
 */
export const DEFAULT_TOTAL_TICKET_LIMIT = null

/** How long a room choice is held during registration (ms). */
export const REGISTRATION_HOLD_MS = 5 * 60 * 1000

export function roomRequiresRoommateName (room) {
  return room === 'Staying with another dancer'
}

export const PAYMENT_PLAN_OPTIONS = [
  {
    value: 1,
    title: '$450 today + remaining balance January 5',
    sub: 'Pay the rest in one go'
  },
  {
    value: 6,
    title: '$450 Today + 6 Payments January - June',
    sub: 'Spread remaining payments across January–June'
  }
]

const INSTALLMENT_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June'
]

/** @param {Date} [now] */
export function ymdInEastern (now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(now)
}

export function installmentFifthIso (year, month) {
  const y = Number(year) || REGISTRATION_YEAR
  const m = Number(month)
  return y + '-' + String(m).padStart(2, '0') + '-05'
}

/**
 * Remaining auto-pay 5ths (Jan–June of festival year) on or after today (Eastern).
 * @param {Date} [now]
 * @param {number} [year]
 * @returns {{ month: number, iso: string, label: string, labelLong: string }[]}
 */
export function remainingInstallmentFifths (now = new Date(), year = REGISTRATION_YEAR) {
  const today = ymdInEastern(now)
  const y = Number(year) || REGISTRATION_YEAR
  const out = []
  for (let m = 1; m <= 6; m++) {
    const iso = installmentFifthIso(y, m)
    if (iso >= today) {
      out.push({
        month: m,
        iso,
        label: INSTALLMENT_MONTH_NAMES[m - 1] + ' 5',
        labelLong: INSTALLMENT_MONTH_NAMES[m - 1] + ' 5th, ' + y
      })
    }
  }
  return out
}

export function isAfterFirstInstallment (now = new Date(), year = REGISTRATION_YEAR) {
  return ymdInEastern(now) > installmentFifthIso(year, 1)
}

export function isPastLastInstallment (now = new Date(), year = REGISTRATION_YEAR) {
  return remainingInstallmentFifths(now, year).length === 0
}

function splitUsdEvenly (totalUsd, n) {
  const count = Math.max(0, Number(n) || 0)
  if (count <= 0) {
    return []
  }
  const total = Math.max(0, Math.round(totalUsd))
  const base = Math.floor(total / count)
  const extra = total - base * count
  const amounts = []
  for (let i = 0; i < count; i++) {
    amounts.push(i < extra ? base + 1 : base)
  }
  return amounts
}

/** Per-month installment, rounded up to a whole dollar when the split is uneven. */
export function installmentEachUsd (remainingCents, n) {
  const count = Math.max(1, Number(n) || 1)
  const cents = Math.max(0, Number(remainingCents) || 0)
  return Math.ceil(cents / (100 * count))
}

export function installmentEachMonthLabel (remainingCents, n) {
  const usd = installmentEachUsd(remainingCents, n)
  return '$' + usd.toLocaleString('en-US') + ' on the 5th each month'
}

/**
 * @param {number} count 0 = pay remaining today; 1 = lump on first remaining 5th (or today after Jan 5); 2–6 = spread
 * @param {number} remainingCents
 * @param {Date} [now]
 * @param {number} [year]
 */
export function resolveInstallmentPlan (
  count,
  remainingCents = REMAINING_CENTS,
  now = new Date(),
  year = REGISTRATION_YEAR
) {
  const fifths = remainingInstallmentFifths(now, year)
  const afterJan = isAfterFirstInstallment(now, year)
  const n = Number(count)
  const totalUsd = Math.max(0, Math.round((Number(remainingCents) || 0) / 100))
  const empty = [0, 0, 0, 0, 0, 0]
  const nextLabel = fifths.length ? fifths[0].labelLong : null

  if (fifths.length === 0 || n === 0 || (afterJan && n === 1)) {
    return {
      payRemainingToday: true,
      amounts: empty,
      installmentCount: 0,
      nextPaymentLabel: null,
      fifths
    }
  }

  if (n === 1) {
    return {
      payRemainingToday: false,
      amounts: [totalUsd, 0, 0, 0, 0, 0],
      installmentCount: 1,
      nextPaymentLabel: nextLabel,
      fifths
    }
  }

  const split = splitUsdEvenly(totalUsd, fifths.length)
  const amounts = empty.slice()
  fifths.forEach((fifth, i) => {
    amounts[fifth.month - 1] = split[i]
  })
  return {
    payRemainingToday: false,
    amounts,
    installmentCount: fifths.length,
    nextPaymentLabel: nextLabel,
    fifths
  }
}

export function nextPaymentEmailLine (now = new Date(), year = REGISTRATION_YEAR, payRemainingToday = false) {
  if (payRemainingToday || remainingInstallmentFifths(now, year).length === 0) {
    return 'The rest of your balance is included in today’s payment.'
  }
  const next = remainingInstallmentFifths(now, year)[0]
  return 'Your next payment will be on ' + next.labelLong + '.'
}

/**
 * Split remaining balance into N whole-dollar installments.
 * First (total % n) payments get floor+1 so the sum matches exactly
 * (e.g. $1,750 / 6 → 292×4 + 291×2).
 * @param {number} count 1 or 6
 * @param {number} [remainingCents]
 * @returns {number[]} length 6; unused slots are 0 for the one-payment plan
 */
export function buildInstallmentAmountsUsd (count, remainingCents = REMAINING_CENTS, now, year) {
  return resolveInstallmentPlan(count, remainingCents, now, year).amounts
}

export function formatUsdFromCents (cents) {
  const n = Number(cents) || 0
  return (n / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

export function depositUsd () {
  return DEPOSIT_CENTS / 100
}

export function last4FromPhone (phone) {
  const digits = String(phone || '').replace(/\D/g, '')
  return digits.slice(-4)
}
